import React, { useEffect, useState } from "react";
import {
  Box, CircularProgress, Button, Typography,
  Table, TableHead, TableRow, TableCell, TableBody, TextField,
  Tabs, Tab, Checkbox, Stack
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { downloadCsv } from "../shared utils/csv";
import { buildStudentTemplateCsv } from "./utils/studentCsv";
import StudentCsvDialog from "./StudentCsvDialog";
import { loadAppSettings, studentAgeRange, ageWarning, ageCheckUnavailableReason } from "../shared utils/appSettings";

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function StudentDetailsTab({
  loading,
  studentDetails = [],
  editable,
  showToast,
  // CSV upload of the three tabs' fields — needs the group and a way back to
  // Bookings for members created with no programme.
  groupId,
  groupName,
  token,
  arrivalDate, // group start, YYYY-MM-DD — ages are taken on that day
  onCsvApplied,
  onGoToBookings,
}) {
  const {
    editRows,
    dirtyIds,
    startEdit,
    cancelEdit,
    changeField,
    changeBool,
    saveRow,
    saveAll
  } = editable || {};

  const [tab, setTab] = useState(0);
  const [csvOpen, setCsvOpen] = useState(false);
  const dirtyCount = dirtyIds?.size || 0;

  // Accepted age range from settings (student.age.min/max). No range → no
  // highlight, and a note saying why, so "nothing flagged" isn't mistaken for "all fine".
  const [settingsState, setSettingsState] = useState(null);
  useEffect(() => {
    loadAppSettings(token).then(setSettingsState);
  }, [token]);
  const ageRange = studentAgeRange(settingsState?.settings);
  const ageUnavailable = ageCheckUnavailableReason(settingsState);
  const ageNote = (dob) => ageWarning(dob, arrivalDate, ageRange);

  // One row per current member with what's known, so the office completes a
  // file rather than starting from a blank one.
  const handleDownloadTemplate = () => {
    const safe = String(groupName || `group-${groupId || ""}`).replace(/[^\w.-]+/g, "_");
    downloadCsv(`${safe}-students.csv`, buildStudentTemplateCsv(studentDetails));
  };

  const csvButtons = groupId ? (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1}>
        <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
          Download template
        </Button>
        <Button size="small" startIcon={<UploadFileIcon />} onClick={() => setCsvOpen(true)}>
          Upload CSV
        </Button>
      </Stack>
      <Typography variant="caption" sx={{ display: "block", opacity: 0.75, mt: 0.5, maxWidth: 900 }}>
        The template has one row per member with a <code>student_id</code>. Keep that column: a
        row <strong>with</strong> an id updates that member; a row <strong>without</strong> one
        fills the next member whose name is still blank (so an agent&apos;s own list works in
        order); rows beyond the group&apos;s size can be added as new members. Blank cells leave
        the current value alone. You&apos;ll see exactly what will happen before anything is
        written.
      </Typography>
      {ageUnavailable && (
        <Typography variant="caption" sx={{ display: "block", opacity: 0.75, mt: 0.25 }}>
          {ageUnavailable}
        </Typography>
      )}
    </Box>
  ) : null;

  const csvDialog = groupId ? (
    <StudentCsvDialog
      open={csvOpen}
      onClose={() => setCsvOpen(false)}
      groupId={groupId}
      token={token}
      arrivalDate={arrivalDate}
      onApplied={onCsvApplied}
      onGoToBookings={onGoToBookings}
    />
  ) : null;
  const canSaveAll = dirtyCount > 0;

  // Run the bulk save and surface a summary toast. saveAll never throws and
  // reports partial failures, so the user can see what saved and retry the rest.
  const handleSaveAll = async () => {
    const res = await saveAll?.();
    if (!res) return;
    if (res.failed?.length) {
      showToast?.(
        `Saved ${res.saved.length}, ${res.failed.length} failed — failed rows kept for retry.`,
        "warning"
      );
    } else if (res.saved?.length) {
      showToast?.(
        `Saved ${res.saved.length} change${res.saved.length === 1 ? "" : "s"}.`,
        "success"
      );
    }
  };


    const permFields = [
    "hasPoolPermission",
    "hasPhotoPermission",
    "hasMedicalPermission",
    "hasHospitalPermission",
    "hasExcursionPermission",
    "hasActivityPermission",
    "hasSupervisionPermission",
  ];

  const getPermValue = (raw, id, field) => {
    const draft = editRows?.[id];
    return !!(draft ? draft[field] : raw?.[field]);
  };

  const pick = (o, ...keys) => {
    for (const k of keys) if (o && o[k] !== undefined && o[k] !== null) return o[k];
    return "";
  };

  const startEditAllRows = () => {
    studentDetails.forEach((raw) => {
      const id = pick(raw, "id");
      if (!editRows?.[id]) startEdit(raw);
    });
  };

  const cancelAllEdits = () => {
    studentDetails.forEach((raw) => {
      const id = pick(raw, "id");
      if (editRows?.[id]) cancelEdit(id);
    });
  };

  const startEditAllPermissions = () => startEditAllRows();

  const cancelAllPermissionsEdits = () => cancelAllEdits();

  // Ensures all rows are in edit mode, then applies the bulk change.
  // setTimeout(0) avoids “draft not created yet” timing issues.
  const setAllPermissions = (value) => {
    startEditAllPermissions();
    setTimeout(() => {
      studentDetails.forEach((raw) => {
        const id = pick(raw, "id");
        permFields.forEach((f) => changeBool(id, f, value));
      });
    }, 0);
  };

  const setPermissionColumn = (field, value) => {
    startEditAllPermissions();
    setTimeout(() => {
      studentDetails.forEach((raw) => {
        const id = pick(raw, "id");
        changeBool(id, field, value);
      });
    }, 0);
  };

  const getColumnState = (field) => {
    if (!studentDetails.length) return { checked: false, indeterminate: false };

    const vals = studentDetails.map((raw) => {
      const id = pick(raw, "id");
      return getPermValue(raw, id, field);
    });

    const all = vals.every(Boolean);
    const any = vals.some(Boolean);

    return { checked: all, indeterminate: any && !all };
  };

  const vCell = (isEditing, raw, id, key, alts = []) =>
    isEditing ? (editRows?.[id]?.[key] ?? "") : pick(raw, key, ...alts);

  return (
    <Box sx={{ overflowX: "auto" }}>
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} /> <span>Loading student details…</span>
        </Box>
      ) : studentDetails.length > 0 ? (
        <>
          {csvButtons}
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Personal" />
            <Tab label="Other Info" />
            <Tab label="Permissions" />
          </Tabs>

          {/* PERSONAL TAB */}
          <TabPanel value={tab} index={0}>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mb: 1, flexWrap: "wrap" }}>
              <Button size="small" variant="outlined" onClick={startEditAllRows}>
                Edit all (Personal)
              </Button>
              <Button size="small" variant="outlined" onClick={cancelAllEdits}>
                Cancel all
              </Button>
              <Button size="small" variant="contained" onClick={handleSaveAll} disabled={!canSaveAll}>
                Save all{canSaveAll ? ` (${dirtyCount})` : ""}
              </Button>
            </Box>
            <Table size="small" sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Surname</TableCell>
                  <TableCell>DOB</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>Nationality</TableCell>
                  <TableCell>English Level</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {studentDetails.map((raw) => {
                  const id = pick(raw, "id");
                  const draft = editRows?.[id];
                  const isEditing = !!draft;

                  return (
                    <TableRow key={`sd-personal-${id}`}>
                      <TableCell>{id}</TableCell>

                      {/* Name */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "studentName", ["student_name"])}
                            onChange={(e) => changeField(id, "studentName", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : vCell(false, raw, id, "studentName", ["student_name"])}
                      </TableCell>

                      {/* Surname */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "studentSurname", ["student_surname"])}
                            onChange={(e) => changeField(id, "studentSurname", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : vCell(false, raw, id, "studentSurname", ["student_surname"])}
                      </TableCell>

                      {/* DOB — highlighted when the age on arrival is outside the accepted range */}
                      <TableCell
                        sx={{ minWidth: 140 }}
                        title={ageNote(vCell(isEditing, raw, id, "studentDob", ["student_dob"])) || undefined}
                      >
                        {isEditing ? (
                          <TextField
                            type="date"
                            value={vCell(true, raw, id, "studentDob", ["student_dob"]) || ""}
                            onChange={(e) => changeField(id, "studentDob", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : (
                          <Box
                            component="span"
                            sx={
                              ageNote(vCell(false, raw, id, "studentDob", ["student_dob"]))
                                ? { color: "warning.dark", fontWeight: 600, borderBottom: "2px solid", borderColor: "warning.main" }
                                : undefined
                            }
                          >
                            {String(vCell(false, raw, id, "studentDob", ["student_dob"]) || "").slice(0, 10)}
                          </Box>
                        )}
                      </TableCell>

                      {/* Gender */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "studentGender", ["student_gender"])}
                            onChange={(e) => changeField(id, "studentGender", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : vCell(false, raw, id, "studentGender", ["student_gender"])}
                      </TableCell>

                      {/* Nationality */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "studentNationality", ["student_nationality"])}
                            onChange={(e) => changeField(id, "studentNationality", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : vCell(false, raw, id, "studentNationality", ["student_nationality"])}
                      </TableCell>

                      {/* English Level */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "englishLevel", ["english_level"])}
                            onChange={(e) => changeField(id, "englishLevel", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : vCell(false, raw, id, "englishLevel", ["english_level"])}
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        {isEditing ? (
                          <>
                            <Button size="small" onClick={() => saveRow(id)} sx={{ mr: 1 }}>
                              Save
                            </Button>
                            <Button size="small" onClick={() => cancelEdit(id)}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button size="small" onClick={() => startEdit(raw)}>Edit</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TabPanel>

          {/* OTHER INFO TAB */}
          <TabPanel value={tab} index={1}>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mb: 1, flexWrap: "wrap" }}>
              <Button size="small" variant="outlined" onClick={startEditAllRows}>
                Edit all (Other Info)
              </Button>
              <Button size="small" variant="outlined" onClick={cancelAllEdits}>
                Cancel all
              </Button>
              <Button size="small" variant="contained" onClick={handleSaveAll} disabled={!canSaveAll}>
                Save all{canSaveAll ? ` (${dirtyCount})` : ""}
              </Button>
            </Box>
            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Allergies</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Emergency Contact</TableCell>
                  <TableCell>Mobile Opt-Out</TableCell>
                  <TableCell>T-shirt Size</TableCell>
                  <TableCell>Mobile Number</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {studentDetails.map((raw) => {
                  const id = pick(raw, "id");
                  const draft = editRows?.[id];
                  const isEditing = !!draft;

                  // Use draft if present so name reflects unsaved edits,
                  // but still NOT editable in this tab.
                  const nameSource = draft || raw;
                  const firstName = pick(nameSource, "studentName", "student_name");
                  const surname = pick(nameSource, "studentSurname", "student_surname");
                  const fullName = `${firstName || ""} ${surname || ""}`.trim();

                  return (
                    <TableRow key={`sd-other-${id}`}>
                      {/* Combined, non-editable student name */}
                      <TableCell>{fullName || id}</TableCell>

                      {/* Allergies */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "allergies")}
                            onChange={(e) => changeField(id, "allergies", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : vCell(false, raw, id, "allergies")}
                      </TableCell>

                      {/* Notes */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "notes")}
                            onChange={(e) => changeField(id, "notes", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : vCell(false, raw, id, "notes")}
                      </TableCell>

                      {/* Emergency Contact */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "emergencyContact", ["emergency_contact"])}
                            onChange={(e) => changeField(id, "emergencyContact", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : vCell(false, raw, id, "emergencyContact", ["emergency_contact"])}
                      </TableCell>

                      {/* Mobile Opt-Out */}
                      <TableCell align="center">
                        {isEditing ? (
                          <input
                            type="checkbox"
                            checked={!!(draft ? draft["mobilePhoneOptOut"] : raw?.["mobilePhoneOptOut"])}
                            onChange={(e) => changeBool(id, "mobilePhoneOptOut", e.target.checked)}
                          />
                        ) : raw?.["mobilePhoneOptOut"] ? "Yes" : "No"}
                      </TableCell>

                      {/* T-shirt */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "tshirtSize", ["TshirtSize","t_shirt_size"])}
                            onChange={(e) => changeField(id, "tshirtSize", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : vCell(false, raw, id, "tshirtSize", ["TshirtSize","t_shirt_size"])}
                      </TableCell>

                      {/* Mobile Number */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={vCell(true, raw, id, "mobileNumber", ["mobile_number"])}
                            onChange={(e) => changeField(id, "mobileNumber", e.target.value)}
                            variant="standard"
                            fullWidth
                          />
                        ) : vCell(false, raw, id, "mobileNumber", ["mobile_number"])}
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        {isEditing ? (
                          <>
                            <Button size="small" onClick={() => saveRow(id)} sx={{ mr: 1 }}>
                              Save
                            </Button>
                            <Button size="small" onClick={() => cancelEdit(id)}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button size="small" onClick={() => startEdit(raw)}>Edit</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TabPanel>

                    {/* PERMISSIONS TAB */}
          <TabPanel value={tab} index={2}>
            {/* Bulk controls */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mb: 1, flexWrap: "wrap" }}>
              <Button size="small" variant="outlined" onClick={startEditAllPermissions}>
                Edit all (Permissions)
              </Button>
              <Button size="small" variant="outlined" onClick={() => setAllPermissions(true)}>
                Tick all
              </Button>
              <Button size="small" variant="outlined" onClick={() => setAllPermissions(false)}>
                Untick all
              </Button>
              <Button size="small" variant="outlined" onClick={cancelAllPermissionsEdits}>
                Cancel all
              </Button>
              <Button size="small" variant="contained" onClick={handleSaveAll} disabled={!canSaveAll}>
                Save all{canSaveAll ? ` (${dirtyCount})` : ""}
              </Button>
            </Box>

            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>

                  {[
                    ["Pool", "hasPoolPermission"],
                    ["Photo", "hasPhotoPermission"],
                    ["Medical", "hasMedicalPermission"],
                    ["Hospital", "hasHospitalPermission"],
                    ["Excursion", "hasExcursionPermission"],
                    ["Activity", "hasActivityPermission"],
                    ["Supervision", "hasSupervisionPermission"],
                  ].map(([label, field]) => {
                    const state = getColumnState(field);
                    return (
                      <TableCell key={field} align="center">
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                          <span>{label}</span>
                          <Checkbox
                            size="small"
                            checked={state.checked}
                            indeterminate={state.indeterminate}
                            onChange={(e) => setPermissionColumn(field, e.target.checked)}
                          />
                        </Box>
                      </TableCell>
                    );
                  })}

                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {studentDetails.map((raw) => {
                  const id = pick(raw, "id");
                  const draft = editRows?.[id];
                  const isEditing = !!draft;

                  const nameSource = draft || raw;
                  const firstName = pick(nameSource, "studentName", "student_name");
                  const surname = pick(nameSource, "studentSurname", "student_surname");
                  const fullName = `${firstName || ""} ${surname || ""}`.trim();

                  return (
                    <TableRow key={`sd-perm-${id}`}>
                      <TableCell>{fullName || id}</TableCell>

                      {permFields.map((field) => (
                        <TableCell key={field} align="center">
                          {isEditing ? (
                            <Checkbox
                              checked={!!(draft ? draft[field] : raw?.[field])}
                              onChange={(e) => changeBool(id, field, e.target.checked)}
                              size="small"
                            />
                          ) : raw?.[field] ? "Yes" : "No"}
                        </TableCell>
                      ))}

                      <TableCell align="right">
                        {isEditing ? (
                          <>
                            <Button size="small" onClick={() => saveRow(id)} sx={{ mr: 1 }}>
                              Save
                            </Button>
                            <Button size="small" onClick={() => cancelEdit(id)}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button size="small" onClick={() => startEdit(raw)}>Edit</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TabPanel>
          {canSaveAll && (
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                zIndex: 10,
                mt: 2,
                py: 1,
                px: 2,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
                bgcolor: "background.paper",
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" sx={{ mr: "auto" }}>
                {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}
              </Typography>

              <Button variant="contained" onClick={handleSaveAll}>
                Save all changes ({dirtyCount})
              </Button>
            </Box>
          )}
        </>
      ) : (
        <>
          {csvButtons}
          <Typography variant="body2">
            <em>No student details available.</em>
          </Typography>
        </>
      )}
      {csvDialog}
    </Box>
  );
}
